import {FC, useEffect, useState} from 'react';
import ModalDefault from '../../../ModalDefault/ModalDefault';
import {Controller, useForm} from "react-hook-form";
import './ModalLeadsOnlyFilters.scss';
import InputDateRange from '../../../InputDateRange/InputDateRange';
import moment from 'moment';
import ReactSelect, {MultiValue} from "react-select";
import {useFinancial} from '../../../../providers/FinancialProvider';
import makeAnimated from "react-select/animated";
import {customStyles} from '../../../../models/SelectCustomStyles';
import {useLeads} from '../../../../providers/Leads/LeadsProvider';
import {contractTypes} from '../../../../models/Leads/ContractTypes';
import {useLeadsCategories} from '../../../../providers/Leads/LeadsCategoriesProvider';
import {ReactInputMaskAny} from "../../../../utils/AnyComponents";
import {useUser} from "../../../../providers/Admin/UserProvider";

interface ModalLeadsOnlyFiltersProps {
    show: boolean,
    onClose?: any,
}

const ModalLeadsOnlyFilters: FC<ModalLeadsOnlyFiltersProps> = (
    {
        show,
        onClose,
    }) => {
    const [productsOptions, setProductsOptions] = useState<any[]>([]);
    const [financialsOptions, setFinancialsOptions] = useState<any[]>([]);
    const [startDate, setStartDate] = useState<any>(moment(new Date()).format('01/MM/YYYY'));
    const [endDate, setEndDate] = useState<any>(moment(new Date()).format('DD/MM/YYYY'));
    const [selectedProducts, setSelectedProducts] = useState<any | null>(null);
    const [selectedFinancials, setSelectedFinancials] = useState<MultiValue<any>>([]);
    const [selectedTypist, setSelectedTypist] = useState<MultiValue<any>>([]);
    const [selectedContractType, setSelectedContractType] = useState<any>();
    const [selectedCategories, setSelectedCategories] = useState<MultiValue<any>>([]);

    const {products, financials, setFinancials} = useFinancial();
    const {seeAll, params, setParams} = useLeads();
    const {categoriesOptions, handleAll: handleAllCategories} = useLeadsCategories();
    const {backofficeTypists: typists} = useUser();

    const {register, handleSubmit, reset, control, setValue} = useForm<any>({});

    const animatedComponents = makeAnimated();

    useEffect(() => {
        if (products?.length > 0) {
            setProductsOptions(
                products.map((item: any) => ({
                    label: item.produto,
                    value: item.sigla.toLowerCase(),
                    id: item.id,
                }))
            );
        }
    }, [products]);

    useEffect(() => {
        if (financials?.length <= 0) {
            setFinancialsOptions([]);
            return;
        }
        setFinancialsOptions(
            financials.map((item: any) => ({
                label: item.financeira,
                value: item.sigla.toLowerCase(),
                id: item.id,
            }))
        );
    }, [financials]);

    useEffect(() => {
        if (selectedProducts) {
            const _financials = products
                .filter((prod: any) => selectedProducts?.value.toLowerCase() === prod.sigla.toLowerCase())
                .map((prod: any) => prod.financeiras)
                .flat();

            setFinancials(
                _financials.filter((value, index, self) =>
                        index === self.findIndex((t) => (
                            t.sigla.toLowerCase() === value.sigla.toLowerCase()
                        ))
                )
            );
        } else {
            const allFinancials = products
                .flatMap((prod: any) => prod.financeiras ?? [])
                .filter((value: any, index: number, self: any[]) =>
                        index === self.findIndex((t: any) =>
                            t.sigla.toLowerCase() === value.sigla.toLowerCase()
                        )
                );
            setFinancials(allFinancials);
        }

        setValue('_produto', selectedProducts?.id ? [selectedProducts?.id] : []);
    }, [selectedProducts, products]);

    useEffect(() => {
        if (selectedFinancials?.length > 0) {
            const selectedFinancialSiglas = selectedFinancials.map((f: any) => f.value.toLowerCase());

            // Filtra apenas os produtos que possuem pelo menos uma das financeiras selecionadas
            const filteredProducts = products.filter((prod: any) =>
                prod.financeiras?.some((fin: any) =>
                    selectedFinancialSiglas.includes(fin.sigla.toLowerCase())
                )
            );

            setProductsOptions(
                filteredProducts.map((item: any) => ({
                    label: item.produto,
                    value: item.sigla.toLowerCase(),
                    id: item.id,
                }))
            );

            // Se o produto atualmente selecionado não pertence a nenhuma financeira selecionada, limpa
            if (
                selectedProducts &&
                !filteredProducts.some(
                    (p: any) => p.sigla.toLowerCase() === selectedProducts.value.toLowerCase()
                )
            ) {
                setSelectedProducts(null);
                setValue('_produto', []);
            }
        } else {
            // Sem financeira selecionada → restaura todos os produtos disponíveis
            setProductsOptions(
                products.map((item: any) => ({
                    label: item.produto,
                    value: item.sigla.toLowerCase(),
                    id: item.id,
                }))
            );
        }
    }, [selectedFinancials, products]);

    useEffect(() => {
        setValue('contractType', selectedContractType?.value || '');
    }, [selectedContractType]);

    useEffect(() => {
        setSelectedProducts(null);
        setSelectedFinancials([]);
        setSelectedTypist([]);
        setSelectedContractType(null);
        setSelectedCategories([]);

        if (show) {
            reset();
            handleAllCategories().then();
        }
    }, [show]);


    const onSubmit = async (data: any) => {
        data.page = 1;
        data.perPage = 15;
        data.startDate = moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        data.endDate = moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        data.cpf = data.cpf?.replace(/\D+/g, '');
        data.withErrors = parseInt(data.withErrors) > 0;
        data.categories = data?.categories && data.categories?.map((item: any) => item.value);
        data._financeira = data?._financeira?.map((item: any) => item.id);
        data.digitador = data?.digitador?.map((item: any) => item.value);

        setParams({...params, ...data});

        onClose(false);
    }

    return (
        <ModalDefault
            className="ModalLeadsOnlyFilters"
            title={'Filtrar Leads'}
            show={show}
            sizeModal={'lg'}
            onClose={onClose}
            buttonText={'Filtrar'}
            handleSubmit={handleSubmit(onSubmit)}
        >
            <div data-testid="ModalLeadsOnlyFilters">
                <form>
                    <div className='row'>
                        <div className={`col-sm-12 col-xs-12`}>
                            <label className='form-control-label'>
                                NOME
                            </label>
                            <div className='form-group'>
                                <input
                                    className='form-control'
                                    placeholder='NOME'
                                    {...register('nome')}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-sm-6 col-xs-12'>
                            <label className='form-control-label'>
                                CPF
                            </label>
                            <div className='form-group'>
                                <ReactInputMaskAny
                                    mask='999.999.999-99'
                                    className='form-control'
                                    placeholder='CPF'
                                    {...register('cpf')}
                                />
                            </div>
                        </div>
                        <div className='col-sm-6 col-xs-12'>
                            <label className='form-control-label'>
                                Celular
                            </label>
                            <div className='form-group'>
                                <ReactInputMaskAny
                                    mask='(99) 99999-9999'
                                    className='form-control'
                                    placeholder='Celular'
                                    {...register('telefone')}
                                />
                            </div>
                        </div>
                    </div>

                    <InputDateRange
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />

                    <div className="row">
                        <div className="col-sm-6 col-xs-12">
                            <label htmlFor='_produto' className='form-control-label'>Produtos</label>
                            <Controller
                                name='_produto'
                                control={control}
                                render={({field: {onChange, name, ref}}) => (
                                    <ReactSelect
                                        ref={ref}
                                        name={name}
                                        isClearable
                                        isSearchable
                                        options={productsOptions}
                                        placeholder='Selecione...'
                                        className='form-control p-0'
                                        value={selectedProducts || ''}
                                        defaultValue={selectedProducts}
                                        components={animatedComponents}
                                        noOptionsMessage={() => 'Não há registros'}
                                        onChange={val => {
                                            setFinancialsOptions([]);

                                            onChange(val || null);
                                            setSelectedProducts(val);
                                        }}
                                        styles={customStyles}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-sm-6 col-xs-12">
                            <label htmlFor='_financeira' className='form-control-label'>Financeiras</label>
                            <Controller
                                name='_financeira'
                                control={control}
                                render={({field: {onChange, name, ref}}) => (
                                    <ReactSelect
                                        ref={ref}
                                        name={name}
                                        isMulti
                                        isClearable
                                        isSearchable
                                        options={financialsOptions}
                                        placeholder='Selecione...'
                                        className='form-control p-0'
                                        value={selectedFinancials || ''}
                                        defaultValue={selectedFinancials}
                                        components={animatedComponents}
                                        noOptionsMessage={() => 'Não há registros'}
                                        onChange={val => {
                                            onChange(val?.length > 0 ? val : null);
                                            setSelectedFinancials(val);
                                        }}
                                        styles={customStyles}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className='row mt-3'>
                        {seeAll && (
                            <>
                                <div className='col'>
                                    <label htmlFor='digitador' className='form-control-label'>Digitador</label>
                                    <Controller
                                        name='digitador'
                                        control={control}
                                        render={({field: {onChange, name, ref}}) => (
                                            <ReactSelect
                                                ref={ref}
                                                name={name}
                                                isMulti
                                                isClearable
                                                isSearchable
                                                options={typists}
                                                placeholder='Selecione...'
                                                className='form-control p-0'
                                                value={selectedTypist || ''}
                                                defaultValue={selectedTypist}
                                                components={animatedComponents}
                                                noOptionsMessage={() => 'Não há registros'}
                                                onChange={val => {
                                                    onChange(val?.length > 0 ? val : null);
                                                    setSelectedTypist(val);
                                                }}
                                                styles={customStyles}
                                            />
                                        )}
                                    />
                                </div>

                                <div className='col'>
                                    <label htmlFor='contractType' className='form-control-label'>Tipo
                                        Contratação</label>
                                    <Controller
                                        name='contractType'
                                        control={control}
                                        render={({field: {onChange, name, ref}}) => (
                                            <ReactSelect
                                                ref={ref}
                                                name={name}
                                                isClearable
                                                isSearchable
                                                options={contractTypes}
                                                placeholder='Selecione...'
                                                className='form-control p-0'
                                                value={selectedContractType || ''}
                                                defaultValue={selectedContractType}
                                                components={animatedComponents}
                                                noOptionsMessage={() => 'Não há registros'}
                                                onChange={val => {
                                                    onChange(val?.length > 0 ? val : null);
                                                    setSelectedContractType(val);
                                                }}
                                                styles={customStyles}
                                            />
                                        )}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="row mt-3">
                        <div className='col-sm-6'>
                            <label htmlFor='categories' className='form-control-label'>Categoria do Lead</label>
                            <Controller
                                name='categories'
                                control={control}
                                render={({field: {onChange, name, ref}}) => (
                                    <ReactSelect
                                        ref={ref}
                                        name={name}
                                        isMulti
                                        isClearable
                                        isSearchable
                                        options={categoriesOptions}
                                        placeholder='Selecione...'
                                        className='form-control p-0'
                                        value={selectedCategories || ''}
                                        defaultValue={selectedCategories}
                                        components={animatedComponents}
                                        noOptionsMessage={() => 'Não há registros'}
                                        onChange={val => {
                                            onChange(val?.length > 0 ? val : null);
                                            setSelectedCategories(val);
                                        }}
                                        styles={customStyles}
                                    />
                                )}
                            />
                        </div>

                        <div className="col-sm-6 form-group">
                            <label>Lead com erro</label>
                            <br/>
                            <div className="form-check form-check-inline mt-1">
                                <input
                                    className="form-check-input"
                                    type='radio'
                                    id="isWithErrors"
                                    value={1}
                                    {...register('withErrors')}
                                />
                                <label className="form-check-label" htmlFor="isWithErrors">Sim</label>
                            </div>
                            <div className="form-check form-check-inline mt-1">
                                <input
                                    className="form-check-input"
                                    type='radio'
                                    id="notWithErrors"
                                    value={0}
                                    {...register('withErrors')}
                                />
                                <label className="form-check-label" htmlFor="notWithErrors">Não</label>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </ModalDefault>
    );
}

export default ModalLeadsOnlyFilters;
